import React from 'react'
import {RefreshControl, View} from 'react-native'
import {Image} from 'expo-image'
import {type AppBskyFeedDefs} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {isArticleRoot, parseArticleFromThread} from '#/lib/news/articleParser'
import {
  FONT_SIZE_VALUES,
  LINE_HEIGHT_VALUES,
  useReaderSettings,
  WIDTH_VALUES,
} from '#/lib/news/readerSettings'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {shareUrl} from '#/lib/sharing'
import {displayAtproto} from '#/lib/strings/identifiers'
import {niceDate} from '#/lib/strings/time'
import {isWeb} from '#/platform/detection'
import {POST_TOMBSTONE, usePostShadow} from '#/state/cache/post-shadow'
import {useAgent} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useBreakpoints} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowOutOfBoxModified_Stroke2_Corner2_Rounded as ShareIcon} from '#/components/icons/ArrowOutOfBox'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RefreshIcon} from '#/components/icons/ArrowRotateCounterClockwise'
import * as Layout from '#/components/Layout'
import {ReaderToolbar} from '#/components/news/ReaderToolbar'
import {BookmarkButton} from '#/components/PostControls/BookmarkButton'
import {Text} from '#/components/Typography'
import {type ArticleBlock, type ArticleComment} from '#/features/news/types'
import {router} from '#/routes'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'NewsArticle'>

export default function NewsArticleScreen({route}: Props) {
  const {_, i18n} = useLingui()
  const navigation = useNavigation()
  const agent = useAgent()
  const {gtMobile, gtTablet} = useBreakpoints()
  const scrollRef = React.useRef<any>(null)
  const scrollOffsetRef = React.useRef(0)
  const restoreOffsetRef = React.useRef<number | null>(null)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [rootPost, setRootPost] =
    React.useState<AppBskyFeedDefs.PostView | null>(null)
  const [article, setArticle] = React.useState<{
    article: ReturnType<typeof parseArticleFromThread>['article']
    comments: ArticleComment[]
  } | null>(null)

  const {fontSize, lineHeight, theme, width} = useReaderSettings()

  const articleUri = React.useMemo(() => {
    if (route.params?.uri) return route.params.uri
    if (route.params?.uriEncoded) {
      try {
        return decodeURIComponent(route.params.uriEncoded)
      } catch {
        return route.params.uriEncoded
      }
    }
    return ''
  }, [route.params?.uri, route.params?.uriEncoded])

  const readerColors = React.useMemo(() => {
    if (theme === 'dark') {
      return {
        background: '#0f1115',
        text: '#f3f4f6',
        muted: '#a8b0bd',
        border: '#202633',
        card: '#171c25',
        badge: '#1f2633',
      }
    }
    if (theme === 'sepia') {
      return {
        background: '#f4ecd8',
        text: '#322a24',
        muted: '#6b5f54',
        border: '#e0d2b9',
        card: '#efe4cd',
        badge: '#e4d7be',
      }
    }
    return {
      background: '#ffffff',
      text: '#141414',
      muted: '#5b5b5b',
      border: '#e6e6e6',
      card: '#f4f5f7',
      badge: '#eef0f3',
    }
  }, [theme])

  const baseFontSize = FONT_SIZE_VALUES[fontSize]
  const baseLineHeight = Math.round(
    baseFontSize * LINE_HEIGHT_VALUES[lineHeight],
  )
  const contentWidth = WIDTH_VALUES[width]
  const horizontalPadding = gtTablet ? 24 : gtMobile ? 16 : 12

  const loadThread = React.useCallback(
    async (restoreScroll?: boolean) => {
      if (restoreScroll) {
        restoreOffsetRef.current = scrollOffsetRef.current
      }
      setError(null)
      setIsLoading(true)
      if (!articleUri) {
        setError(_(msg`Missing article URI.`))
        setArticle(null)
        setRootPost(null)
        setIsLoading(false)
        return
      }
      try {
        if (__DEV__) {
          console.info('[NewsArticle] resolved uri', articleUri)
        }
        const res = await agent.getPostThread({
          uri: articleUri,
          depth: 10,
        })
        if (!res.success) {
          if (__DEV__) {
            console.warn('[NewsArticle] thread fetch failed', res)
          }
          setError(_(msg`Unable to load this article. Please try again.`))
          setArticle(null)
          setRootPost(null)
          return
        }

        const thread = res.data.thread as {
          post?: AppBskyFeedDefs.PostView
          replies?: unknown
        }
        if (!thread?.post) {
          setError(_(msg`This post is not available.`))
          setArticle(null)
          setRootPost(null)
          return
        }

        if (!isArticleRoot(thread.post)) {
          setError(_(msg`Not a TellusArticle.`))
          setRootPost(thread.post)
          setArticle(null)
          return
        }

        const parsed = parseArticleFromThread(thread)
        setRootPost(thread.post)
        setArticle(parsed)
        if (!parsed.article) {
          setError(_(msg`This thread does not match the article format.`))
        }
      } catch (err) {
        if (__DEV__) {
          console.warn('[NewsArticle] thread fetch error', err)
        }
        setError(_(msg`Unable to load this article. Please try again.`))
        setArticle(null)
        setRootPost(null)
      } finally {
        setIsLoading(false)
      }
    },
    [agent, articleUri, _],
  )

  React.useEffect(() => {
    loadThread()
  }, [loadThread])

  React.useEffect(() => {
    if (restoreOffsetRef.current !== null && scrollRef.current) {
      const offset = restoreOffsetRef.current
      restoreOffsetRef.current = null
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({y: offset, animated: false})
      })
    }
  }, [article])

  const onRefresh = React.useCallback(async () => {
    setIsRefreshing(true)
    await loadThread(true)
    setIsRefreshing(false)
  }, [loadThread])

  const articlePath = React.useMemo(() => {
    const matchedRoute = router
      .matchName('NewsArticle')
      ?.build({uri: articleUri})
    return matchedRoute ?? `/news/article?uri=${encodeURIComponent(articleUri)}`
  }, [articleUri])

  const onShare = React.useCallback(() => {
    shareUrl(`https://tellus.app${articlePath}`)
  }, [articlePath])

  const onScroll = React.useCallback(event => {
    scrollOffsetRef.current = event.nativeEvent.contentOffset.y
  }, [])

  const articleData = article?.article
  const authorHandle = articleData?.author?.handle ?? ''
  const authorDisplayName =
    articleData?.author?.displayName || displayAtproto(authorHandle)
  const authorHandleLabel = authorHandle ? displayAtproto(authorHandle) : ''
  const comments = article?.comments ?? []
  const showLoading = isLoading && !articleData && !error

  return (
    <Layout.Screen testID="newsArticleScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Actualités</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <Layout.Content
        ref={scrollRef}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
        }
        onScroll={onScroll}
        scrollEventThrottle={16}
        centerStyle={{maxWidth: 980}}
        contentContainerStyle={[
          a.pb_xl,
          {
            backgroundColor: readerColors.background,
            paddingHorizontal: horizontalPadding,
          },
        ]}>
        {showLoading ? (
          <View style={[a.py_xl, a.align_center]}>
            <Text style={[a.text_md, {color: readerColors.muted}]}>
              <Trans>Loading article…</Trans>
            </Text>
          </View>
        ) : error ? (
          <View style={[a.py_xl, a.gap_md]}>
            <Text style={[a.text_md, {color: readerColors.muted}]}>
              {error}
            </Text>
            <View style={[a.flex_row, a.gap_sm, a.flex_wrap]}>
              <Button
                label={_(msg`Go back`)}
                onPress={() => navigation.goBack()}
                variant="ghost"
                color="secondary"
                shape="round">
                <ButtonText>
                  <Trans>Go back</Trans>
                </ButtonText>
              </Button>
              <Button
                label={_(msg`Retry`)}
                onPress={onRefresh}
                variant="solid"
                color="primary"
                shape="round">
                <ButtonText>
                  <Trans>Retry</Trans>
                </ButtonText>
              </Button>
            </View>
          </View>
        ) : articleData ? (
          <>
            {articleData.cover ? (
              <View style={[a.pt_lg]}>
                <ArticleImage
                  uri={articleData.cover.fullsize || articleData.cover.thumb}
                  mode="cover"
                  style={{aspectRatio: 16 / 9}}
                  rounded
                />
              </View>
            ) : null}
            <View style={[a.pt_lg, a.gap_lg]}>
              <View style={[a.gap_sm]}>
                <Text
                  style={[
                    a.font_heavy,
                    {color: readerColors.text, fontSize: baseFontSize + 12},
                  ]}>
                  {articleData.title}
                </Text>
                {articleData.subtitle ? (
                  <Text
                    style={[
                      a.text_md,
                      {color: readerColors.muted, lineHeight: baseLineHeight},
                    ]}>
                    {articleData.subtitle}
                  </Text>
                ) : null}
                <View
                  style={[
                    a.flex_row,
                    a.align_center,
                    a.justify_between,
                    a.gap_md,
                    !gtMobile && a.flex_wrap,
                  ]}>
                  <View style={[a.flex_row, a.align_center, a.gap_sm, a.flex_1]}>
                    <UserAvatar
                      type="user"
                      size={36}
                      avatar={articleData.author?.avatar}
                    />
                    <View style={[a.flex_1]}>
                      <Text style={[a.font_bold, {color: readerColors.text}]}>
                        {authorDisplayName}
                      </Text>
                      {articleData.publishedAt || authorHandleLabel ? (
                        <Text style={[a.text_xs, {color: readerColors.muted}]}>
                          {authorHandleLabel || null}
                          {authorHandleLabel && articleData.publishedAt
                            ? ' · '
                            : null}
                          {articleData.publishedAt
                            ? niceDate(i18n, articleData.publishedAt)
                            : null}
                        </Text>
                      ) : null}
                    </View>
                  </View>

                  <View
                    style={[
                      a.flex_row,
                      a.align_center,
                      a.gap_sm,
                      !gtMobile && a.justify_end,
                    ]}>
                    <ReaderToolbar />
                    {rootPost ? <ArticleBookmark post={rootPost} /> : null}
                    <Button
                      label={_(msg`Share`)}
                      size="small"
                      variant="ghost"
                      color="secondary"
                      shape="round"
                      onPress={onShare}>
                      <ButtonIcon icon={ShareIcon} />
                    </Button>
                    <Button
                      label={_(msg`Refresh`)}
                      size="small"
                      variant="ghost"
                      color="secondary"
                      shape="round"
                      onPress={onRefresh}>
                      <ButtonIcon icon={RefreshIcon} />
                    </Button>
                  </View>
                </View>

                <View
                  style={[
                    a.w_full,
                    {height: 1, backgroundColor: readerColors.border},
                  ]}
                />
              </View>

              <View
                style={[
                  a.mx_auto,
                  {maxWidth: Math.min(contentWidth, 680), width: '100%'},
                ]}>
                {articleData.blocks.map((block, index) => (
                  <ArticleBlockView
                    key={`${block.type}-${index}`}
                    block={block}
                    colors={readerColors}
                    baseFontSize={baseFontSize}
                    baseLineHeight={baseLineHeight}
                  />
                ))}
              </View>
            </View>

            <View style={[a.pt_2xl, a.gap_md]}>
              <Text
                style={[a.text_lg, a.font_heavy, {color: readerColors.text}]}>
                <Trans>Comments</Trans>
              </Text>
              {comments.length === 0 ? (
                <Text style={[a.text_sm, {color: readerColors.muted}]}>
                  <Trans>No comments yet.</Trans>
                </Text>
              ) : (
                <View style={[a.gap_md]}>
                  {comments.map(comment => (
                    <CommentCard
                      key={comment.uri}
                      comment={comment}
                      colors={readerColors}
                    />
                  ))}
                </View>
              )}
            </View>
          </>
        ) : null}
      </Layout.Content>
    </Layout.Screen>
  )
}

function ArticleBookmark({post}: {post: AppBskyFeedDefs.PostView}) {
  const shadow = usePostShadow(post)
  if (shadow === POST_TOMBSTONE) return null
  return <BookmarkButton post={shadow} logContext="Post" />
}

function ArticleBlockView({
  block,
  colors,
  baseFontSize,
  baseLineHeight,
}: {
  block: ArticleBlock
  colors: {
    background: string
    text: string
    muted: string
    border: string
    card: string
    badge: string
  }
  baseFontSize: number
  baseLineHeight: number
}) {
  return (
    <View style={[a.mb_lg, a.gap_sm]}>
      {block.text ? (
        <Text
          style={[
            {color: colors.text, fontSize: baseFontSize},
            {lineHeight: baseLineHeight},
          ]}>
          {block.text}
        </Text>
      ) : null}
      {block.images?.length ? (
        <View style={[a.gap_sm]}>
          {block.images.map((image, index) => (
            <ArticleImage
              key={`${image.fullsize}-${index}`}
              uri={image.fullsize || image.thumb}
              mode="contain"
              aspectRatio={getImageAspectRatio(image)}
              rounded
            />
          ))}
        </View>
      ) : null}
    </View>
  )
}

function CommentCard({
  comment,
  colors,
}: {
  comment: ArticleComment
  colors: {
    text: string
    muted: string
    card: string
  }
}) {
  const indent = Math.min(comment.depth, 3) * 16
  const authorLabel =
    comment.author?.displayName ||
    displayAtproto(comment.author?.handle || '') ||
    'Unknown'
  return (
    <View
      style={[
        a.p_md,
        a.rounded_md,
        a.gap_sm,
        {
          backgroundColor: colors.card,
          marginLeft: indent,
        },
      ]}>
      <View style={[a.flex_row, a.align_center, a.gap_sm]}>
        <UserAvatar type="user" size={28} avatar={comment.author?.avatar} />
        <View style={[a.flex_1]}>
          <Text style={[a.font_bold, {color: colors.text}]}>
            {authorLabel}
          </Text>
          {comment.createdAt ? (
            <Text style={[a.text_xs, {color: colors.muted}]}>
              {new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(comment.createdAt))}
            </Text>
          ) : null}
        </View>
      </View>
      {comment.text ? (
        <Text style={[a.text_sm, {color: colors.text}]}>{comment.text}</Text>
      ) : null}
      {comment.images?.length ? (
        <View style={[a.gap_sm]}>
          {comment.images.map((image, index) => (
            <ArticleImage
              key={`${image.fullsize}-${index}`}
              uri={image.fullsize || image.thumb}
              mode="contain"
              aspectRatio={getImageAspectRatio(image)}
              rounded
            />
          ))}
        </View>
      ) : null}
    </View>
  )
}

function getImageAspectRatio(image: {
  aspectRatio?: {width: number; height: number}
}) {
  const dims = image.aspectRatio
  if (!dims?.width || !dims?.height) return undefined
  const ratio = dims.width / dims.height
  return Number.isFinite(ratio) ? ratio : undefined
}

function ArticleImage({
  uri,
  mode,
  aspectRatio,
  style,
  rounded,
}: {
  uri?: string
  mode: 'cover' | 'contain'
  aspectRatio?: number
  style?: Record<string, any>
  rounded?: boolean
}) {
  const [hidden, setHidden] = React.useState(false)

  const onError = React.useCallback(() => {
    if (__DEV__) {
      console.warn('[NewsArticle] image failed', uri)
    }
    setHidden(true)
  }, [uri])

  if (!uri || hidden) return null

  const wrapperStyle = [
    a.w_full,
    rounded ? a.rounded_md : null,
    a.overflow_hidden,
    style,
  ]

  if (isWeb) {
    // Use a plain <img> on web to avoid programmatic fetches to the CDN.
    return (
      <View style={wrapperStyle}>
        {/* @ts-ignore react-native-web supports DOM tags */}
        <img
          src={uri}
          style={{
            width: '100%',
            height: style?.aspectRatio ? '100%' : 'auto',
            display: 'block',
            objectFit: mode,
          }}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={onError}
          alt=""
        />
      </View>
    )
  }

  return (
    <View style={wrapperStyle}>
      <Image
        source={{uri}}
        style={[
          a.w_full,
          aspectRatio
            ? {aspectRatio}
            : style?.aspectRatio
              ? {aspectRatio: style.aspectRatio}
              : {height: 240},
        ]}
        contentFit={mode}
        accessibilityIgnoresInvertColors
        onError={onError}
      />
    </View>
  )
}
